import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowRight, ExternalLink, Star, Grid, List, LayoutGrid, PhoneCall, Sparkles, Compass, Workflow } from 'lucide-react';
import { motion } from 'framer-motion';
import { publicApi } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import { getCategoryHeroImgBySlug } from '../lib/images';
import ToolLogo from '../components/ToolLogo';
import { buildToolGoUrl } from '../utils/affiliateLinks';
const HOMEPAGE_CACHE_KEY = 'wowrolla_homepage_data_cache_v1';
const CATEGORY_PAGE_CACHE_KEY = 'wowrolla_categories_page_cache_v1';

const slugify = (value = '') =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const readHomepageCache = () => {
  try {
    return JSON.parse(localStorage.getItem(HOMEPAGE_CACHE_KEY) || 'null') || null;
  } catch {
    return null;
  }
};

const readCategoriesCache = () => {
  try {
    return JSON.parse(localStorage.getItem(CATEGORY_PAGE_CACHE_KEY) || 'null') || null;
  } catch {
    return null;
  }
};

const resolveCategoryBySlug = (slug, categories = []) => {
  if (!slug || !Array.isArray(categories)) return null;
  const normalized = slugify(slug);
  return (
    categories.find((cat) => slugify(cat?.slug || '') === normalized) ||
    categories.find((cat) => slugify(cat?.name || '') === normalized) ||
    null
  );
};

const filterToolsForCategory = (category, slug, tools = []) => {
  if (!Array.isArray(tools) || !tools.length) return [];
  const normalizedSlug = slugify(slug || category?.slug || category?.name || '');
  const categoryId = category?.id;
  const categoryName = String(category?.name || '').toLowerCase();

  return tools.filter((tool) => {
    const toolCategoryId = tool?.category_id || tool?.category_slug;
    const toolCategorySlug = slugify(tool?.category_slug || tool?.category_name || '');
    const toolCategoryName = String(tool?.category_name || '').toLowerCase();

    if (categoryId && toolCategoryId === categoryId) return true;
    if (normalizedSlug && toolCategorySlug === normalizedSlug) return true;
    if (categoryName && toolCategoryName === categoryName) return true;
    return false;
  });
};

const buildFallbackCategoryFromTools = (slug, tools = []) => {
  const normalizedSlug = slugify(slug || '');
  const matched = tools.filter((tool) => {
    const toolSlug = slugify(tool?.category_slug || tool?.category_name || '');
    return toolSlug === normalizedSlug;
  });

  if (!matched.length) return null;

  const first = matched[0] || {};
  return {
    id: first.category_id || first.category_slug || normalizedSlug,
    name: first.category_name || first.category_slug || 'AI Tools',
    slug: first.category_slug || normalizedSlug,
    description: `Explore AI tools for ${first.category_name || first.category_slug || normalizedSlug}.`,
    meta_title: null,
    meta_description: null,
  };
};

const collectUseCaseChips = (tools = []) => {
  const counts = new Map();
  tools.forEach((tool) => {
    (tool.best_for || []).slice(0, 3).forEach((entry) => {
      const cleaned = String(entry || '').trim();
      if (!cleaned) {
        return;
      }
      counts.set(cleaned, (counts.get(cleaned) || 0) + 1);
    });
  });
  return [...counts.entries()]
    .sort((left, right) => right[1] - left[1])
    .slice(0, 5)
    .map(([label]) => label);
};

const CategoryPage = () => {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [tools, setTools] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const { getCategoryColor, theme } = useTheme();

  const hydrateFromCache = useCallback(() => {
    const homeCache = readHomepageCache();
    const categoryCache = readCategoriesCache();
    const categoriesFromCache = Array.isArray(categoryCache?.categories) && categoryCache.categories.length
      ? categoryCache.categories
      : (homeCache?.categories || []);

    const cachedCategory = resolveCategoryBySlug(slug, categoriesFromCache)
      || buildFallbackCategoryFromTools(slug, homeCache?.allTools || []);

    if (!cachedCategory) return false;

    const cachedCategoryTools = categoryCache?.categoryTools?.[cachedCategory.id] || [];
    const fallbackCacheTools = filterToolsForCategory(cachedCategory, slug, homeCache?.allTools || []);
    const cachedTools = cachedCategoryTools.length ? cachedCategoryTools : fallbackCacheTools;

    setCategory(cachedCategory);
    setTools(cachedTools);
    setAllCategories(categoriesFromCache.length ? categoriesFromCache : [cachedCategory]);
    setLoading(false);
    return true;
  }, [slug]);

  const loadCategoryData = useCallback(async (isCancelled = () => false) => {
    const hasImmediate = hydrateFromCache();
    if (!hasImmediate) {
      setLoading(true);
    }

    try {
      const homeCache = readHomepageCache();
      const categoryCache = readCategoriesCache();
      const [catResponse, toolsResponse, allCatsResponse] = await Promise.allSettled([
        publicApi.getCategoryBySlug(slug),
        publicApi.getToolsByCategory(slug),
        publicApi.getCategories(),
      ]);

      if (isCancelled()) return;

      const allTools = homeCache?.allTools || [];

      const categories = allCatsResponse.status === 'fulfilled'
        ? (allCatsResponse.value.data || [])
        : (categoryCache?.categories || homeCache?.categories || []);

      const apiCategory = catResponse.status === 'fulfilled' ? catResponse.value.data : null;
      const fallbackCategory = resolveCategoryBySlug(slug, categories);
      const derivedCategory = buildFallbackCategoryFromTools(slug, allTools);
      const resolvedCategory = apiCategory || fallbackCategory || derivedCategory;

      const apiTools = toolsResponse.status === 'fulfilled' ? (toolsResponse.value.data || []) : [];
      const cachedTools = categoryCache?.categoryTools?.[resolvedCategory?.id] || [];
      const fallbackTools = filterToolsForCategory(resolvedCategory, slug, allTools);
      const resolvedTools = apiTools.length ? apiTools : (cachedTools.length ? cachedTools : fallbackTools);

      setCategory(resolvedCategory);
      setTools(resolvedTools);
      setAllCategories(categories.length ? categories : (resolvedCategory ? [resolvedCategory] : []));
    } catch (error) {
      console.error('Failed to load category:', error);
      const homeCache = readHomepageCache();
      const categoryCache = readCategoriesCache();
      const allTools = homeCache?.allTools || [];
      const fallbackCategory = resolveCategoryBySlug(slug, categoryCache?.categories || homeCache?.categories || []) || buildFallbackCategoryFromTools(slug, allTools);
      if (isCancelled()) return;
      setCategory(fallbackCategory);
      setTools(filterToolsForCategory(fallbackCategory, slug, allTools));
      setAllCategories(categoryCache?.categories || homeCache?.categories || (fallbackCategory ? [fallbackCategory] : []));
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, [hydrateFromCache, slug]);

  useEffect(() => {
    let cancelled = false;
    loadCategoryData(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [loadCategoryData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-base)' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--accent-primary)' }}></div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-base)' }}>
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Category not found</h2>
          <Link to="/" style={{ color: 'var(--accent-primary)' }}>Return to homepage</Link>
        </div>
      </div>
    );
  }

  const catIndex = allCategories.findIndex(c => c.id === category.id);
  const color = getCategoryColor(catIndex >= 0 ? catIndex : 0, allCategories.length);
  const isLight = theme === 'light';
  const template = category.template || 'default';
  const useCaseChips = collectUseCaseChips(tools);
  const heroImage = category.background_image || getCategoryHeroImgBySlug(category.slug || slug, catIndex >= 0 ? catIndex : 0);
  const heroOverlay = isLight
    ? `linear-gradient(135deg, rgba(255,255,255,0.95), rgba(251,254,255,0.90), ${color.bg}22)`
    : `linear-gradient(to bottom, rgba(8,8,18,0.58), rgba(8,8,18,0.94))`;
  const heroStatCardBackground = isLight ? 'rgba(255,255,255,0.74)' : 'rgba(0,0,0,0.18)';
  const heroStatCardBorder = isLight ? '1px solid rgba(15,23,42,0.08)' : '1px solid rgba(255,255,255,0.08)';
  const heroStatNumberColor = isLight ? 'var(--text-primary)' : '#ffffff';

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": category.meta_title || `Best ${category.name} - AI Tools`,
    "description": category.meta_description || category.description,
    "url": `https://wowrolla.ca/categories/${category.slug}`,
    "numberOfItems": tools.length,
    "itemListElement": tools.map((tool, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "SoftwareApplication",
        "name": tool.name,
        "description": tool.tagline,
        "url": `https://wowrolla.ca/tools/${tool.slug}`,
        "applicationCategory": category.name,
        ...(tool.rating && { "aggregateRating": { "@type": "AggregateRating", "ratingValue": tool.rating.overall, "bestRating": 5 } })
      }
    }))
  };

  return (
    <div className="min-h-screen pt-24 page-bg-shell page-bg-robot" data-testid="category-page">
      <Helmet>
        <title>{category.meta_title || `${category.name} - Best AI Tools | Wowrolla`}</title>
        <meta name="description" content={category.meta_description || category.description} />
        <meta property="og:title" content={category.meta_title || `${category.name} - Wowrolla`} />
        <meta property="og:description" content={category.meta_description || category.description} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero Section with optional background */}
      <section
        className="relative overflow-hidden"
        style={{
          background: `${heroOverlay}, url(${heroImage}) center/cover no-repeat`,
        }}
      >
        <div className="container mx-auto px-6 py-10 lg:py-12">
          <Link to="/" className="inline-flex items-center transition-colors mb-8"
            style={{ color: 'var(--text-secondary)' }}
            onMouseEnter={e => e.currentTarget.style.color = color.accent}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}
            data-testid="back-link"
          >
            <ArrowLeft size={16} className="mr-2" /> Back to Home
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6 items-end">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4" style={{ background: isLight ? 'rgba(255,255,255,0.68)' : 'rgba(0,0,0,0.22)', border: heroStatCardBorder }}>
                  <Sparkles size={14} style={{ color: color.accent }} />
                  <span className="text-xs uppercase tracking-[0.18em] font-mono" style={{ color: color.accent }}>Category briefing</span>
                </div>
                {category.cover_image && (
                  <img src={category.cover_image} alt={category.name} className="w-16 h-16 object-contain mb-5 rounded-lg" />
                )}
                <h1 className="text-4xl sm:text-5xl font-black mb-4" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }} data-testid="category-title">
                  {category.name}
                </h1>
                <p className="text-base sm:text-lg max-w-3xl" style={{ color: 'var(--text-secondary)' }}>
                  {category.description}
                </p>
                {useCaseChips.length > 0 && (
                  <div className="mt-5 flex flex-wrap gap-2">
                    {useCaseChips.map((chip) => (
                      <span key={chip} className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs" style={{ color: color.accent, background: `${color.accent}14`, border: `1px solid ${color.accent}28` }}>
                        <Workflow size={12} /> {chip}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <Link to={`/best?sort=overall&usecase=${category.slug}`} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm" style={{ background: `${color.accent}14`, color: color.accent, border: `1px solid ${color.accent}28` }}>
                    <Compass size={14} /> View rankings <ArrowRight size={13} />
                  </Link>
                  <span className="text-sm font-mono" style={{ color: 'var(--text-muted)' }}>
                    {tools.length} tool{tools.length !== 1 ? 's' : ''} available
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl p-4" style={{ background: heroStatCardBackground, border: heroStatCardBorder }}>
                  <div className="text-xs font-mono uppercase tracking-[0.18em] mb-2" style={{ color: color.accent }}>Inventory</div>
                  <div className="text-2xl font-black" style={{ color: heroStatNumberColor }}>{tools.length}</div>
                </div>
                <div className="rounded-2xl p-4" style={{ background: heroStatCardBackground, border: heroStatCardBorder }}>
                  <div className="text-xs font-mono uppercase tracking-[0.18em] mb-2" style={{ color: color.accent }}>Use cases</div>
                  <div className="text-2xl font-black" style={{ color: heroStatNumberColor }}>{useCaseChips.length || '1+'}</div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-4">
              <div className="flex gap-1">
                {['grid', 'list'].map(mode => (
                  <button key={mode} onClick={() => setViewMode(mode)}
                    className="p-2 rounded transition-colors"
                    style={{ background: viewMode === mode ? color.bg : 'transparent', color: viewMode === mode ? color.accent : 'var(--text-muted)' }}
                    data-testid={`view-mode-${mode}`}
                  >
                    {mode === 'grid' ? <LayoutGrid size={16} /> : <List size={16} />}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tools */}
      <section className="container mx-auto px-6 pb-24 pt-8">
        {/* Consulting CTA Banner */}
        <div className="mb-8 px-5 py-4 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          style={{ background: 'rgba(255,232,0,0.06)', border: '1px solid rgba(255,232,0,0.25)' }}
          data-testid="consulting-cta-banner"
        >
          <div className="flex items-start gap-3">
            <PhoneCall size={18} className="text-cyber-yellow flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-white">Need expert help choosing the right {category?.name} tool?</p>
              <p className="text-xs text-text-secondary mt-0.5">Our consulting service maps your workflow and delivers a clear buying recommendation - not a list of features.</p>
            </div>
          </div>
          <Link to="/consulting#book" className="flex-shrink-0 px-4 py-2 text-xs font-bold rounded-full transition-all hover:scale-105 whitespace-nowrap"
            style={{ border: '1px solid rgba(255,232,0,0.65)', color: '#FFE400', background: 'rgba(255,232,0,0.08)' }}>
            Book Consulting
          </Link>
        </div>

        {tools.length === 0 ? (
          <div className="text-center py-16" style={{ color: 'var(--text-secondary)' }}>
            <p>No tools available in this category yet.</p>
          </div>
        ) : template === 'showcase' ? (
          <ShowcaseLayout tools={tools} color={color} />
        ) : viewMode === 'list' ? (
          <ListLayout tools={tools} color={color} />
        ) : (
          <GridLayout tools={tools} color={color} />
        )}
      </section>
    </div>
  );
};

const buildGoUrl = (tool) => buildToolGoUrl(tool);

const GridLayout = ({ tools, color }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tools-grid">
    {tools.map((tool, index) => (
      <motion.div key={tool.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.05 }} className="terminal-grid card-hover overflow-hidden" data-testid={`tool-card-${tool.slug}`}>
        {tool.cover_image && (
          <div className="h-40 overflow-hidden">
            <img src={tool.cover_image} alt={tool.name} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-6">
          <div className="flex items-start gap-3 mb-3">
            <ToolLogo domain={tool.website_url} alt={tool.name} className="flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{tool.name}</h3>
              {tool.tagline && <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{tool.tagline}</p>}
            </div>
          </div>
          {tool.rating && (
            <div className="flex items-center gap-2 mb-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={14} style={{ color: i < Math.floor(tool.rating.overall) ? color.accent : 'var(--text-muted)', fill: i < Math.floor(tool.rating.overall) ? color.accent : 'none' }} />
              ))}
              <span className="text-sm font-mono" style={{ color: 'var(--text-muted)' }}>{tool.rating.overall.toFixed(1)}</span>
            </div>
          )}
          {tool.pricing_tiers?.[0] && (
            <div className="mb-4">
              <span className="text-sm font-mono" style={{ color: color.accent }}>From {tool.pricing_tiers[0].price}</span>
            </div>
          )}
          <div className="flex gap-2 mt-auto">
            <Link to={`/tools/${tool.slug}`} className="btn-secondary text-sm flex-1 text-center">Learn More</Link>
            <Link to="/find" className="btn-secondary text-sm flex-1 text-center" data-testid={`finder-link-${tool.slug}`}>
              Not sure? Try finder
            </Link>
            <a href={buildGoUrl(tool)}
              target="_blank" rel="sponsored nofollow noopener noreferrer"
              className="btn-primary text-sm flex items-center gap-1" data-testid={`tool-visit-${tool.slug}`}>
              Visit <ExternalLink size={14} />
            </a>
          </div>
        </div>
      </motion.div>
    ))}
  </div>
);

const ListLayout = ({ tools, color }) => (
  <div className="space-y-4" data-testid="tools-list">
    {tools.map((tool, index) => (
      <motion.div key={tool.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4, delay: index * 0.05 }}
        className="terminal-grid p-5 flex items-center gap-6 card-hover" data-testid={`tool-card-${tool.slug}`}>
        <ToolLogo domain={tool.website_url} alt={tool.name} className="w-14 h-14 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{tool.name}</h3>
          <p className="text-sm truncate" style={{ color: 'var(--text-secondary)' }}>{tool.tagline}</p>
          <div className="flex items-center gap-4 mt-2">
            {tool.rating && (
              <div className="flex items-center gap-1">
                <Star size={12} style={{ color: color.accent, fill: color.accent }} />
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{tool.rating.overall.toFixed(1)}</span>
              </div>
            )}
            {tool.pricing_tiers?.[0] && <span className="text-xs font-mono" style={{ color: color.accent }}>{tool.pricing_tiers[0].price}</span>}
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Link to={`/tools/${tool.slug}`} className="btn-secondary text-sm">Details</Link>
          <Link to="/find" className="btn-secondary text-sm" data-testid={`finder-list-link-${tool.slug}`}>Try finder</Link>
          <a href={buildGoUrl(tool)}
            target="_blank" rel="sponsored nofollow noopener noreferrer"
            className="btn-primary text-sm flex items-center gap-1">
            Visit <ExternalLink size={14} />
          </a>
        </div>
      </motion.div>
    ))}
  </div>
);

const ShowcaseLayout = ({ tools, color }) => (
  <div className="space-y-12" data-testid="tools-showcase">
    {tools.map((tool, index) => (
      <motion.div key={tool.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }}
        className="terminal-grid overflow-hidden" data-testid={`tool-card-${tool.slug}`}>
        {(tool.cover_image || tool.background_image) && (
          <div className="h-56 overflow-hidden">
            <img src={tool.cover_image || tool.background_image} alt={tool.name} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-8">
          <div className="flex items-start gap-4 mb-4">
            <ToolLogo domain={tool.website_url} alt={tool.name} className="w-16 h-16" />
            <div>
              <h3 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>{tool.name}</h3>
              <p className="text-base mt-1" style={{ color: color.accent }}>{tool.tagline}</p>
            </div>
          </div>
          <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>{tool.description}</p>
          {tool.features?.length > 0 && (
            <div className="grid grid-cols-2 gap-2 mb-6">
              {tool.features.slice(0, 6).map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color.accent }}></div>
                  <span style={{ color: 'var(--text-secondary)' }}>{f}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-3">
            <Link to={`/tools/${tool.slug}`} className="btn-secondary">Full Review</Link>
            <Link to="/find" className="btn-secondary" data-testid={`finder-showcase-link-${tool.slug}`}>Not sure? Try finder</Link>
            <a href={buildGoUrl(tool)}
              target="_blank" rel="sponsored nofollow noopener noreferrer"
              className="btn-primary flex items-center gap-2">
              {tool.affiliate_link.my_referral_link ? 'Join via My Link' : 'Visit Tool'} <ExternalLink size={16} />
            </a>
          </div>
        </div>
      </motion.div>
    ))}
  </div>
);

export default CategoryPage;


